'use client'

import { useCallback, useEffect, useState } from 'react'
import { useTranslations } from 'next-intl'
import { Upload, FileText, Eye, ExternalLink } from 'lucide-react'

import MarkdownFullscreenViewer from '@/components/markdown-fullscreen-viewer'
import { exampleMarkdown } from '@/constants/example-markdown'
import {
  MarkdownConverterShell,
  useMarkdownConverterCommon,
} from '@/components/markdown-converter-shell'
import { trackGtagEvent } from '@/lib/gtag'
import { trackPlausibleEvent } from '@/lib/plausible'
import { VerificationModal } from '@/components/verification-modal'
import InAppDownloadModal from '@/components/in-app-download-modal'
import { useInAppDownloadGuard } from '@/lib/use-in-app-download-guard'
import { DocumentExportSettingsButton } from '@/components/document-export-settings-button'
import {
  DEFAULT_DOCUMENT_EXPORT_SETTINGS,
  type DocumentExportSettings,
} from '@/lib/document-export-settings'
import { trackTurnstileEvent } from '@/lib/turnstile-analytics'
import {
  isPdfMarkdownTooLarge,
  PDF_MARKDOWN_MAX_SIZE_BYTES,
} from '@/lib/pdf/limits'

const PDF_ENDPOINT = '/api/pdf'
const PDF_TO_MARKDOWN_URL = 'https://pdf-to-markdown.app/'
const TURNSTILE_ORIGIN = 'https://challenges.cloudflare.com'

export default function MarkdownToPdfConverter() {
  const t = useTranslations('MarkdownToPdfConverter')
  const tc = useTranslations('common')
  const {
    info: inAppInfo,
    isOpen: isInAppModalOpen,
    guardDownload,
    closeModal: closeInAppModal,
    continueWithDownload,
  } = useInAppDownloadGuard()
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const [turnstileRefreshKey, setTurnstileRefreshKey] = useState(0)
  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false)
  const [pendingAutoDownload, setPendingAutoDownload] = useState(false)
  const [pendingVerificationMarkdown, setPendingVerificationMarkdown] =
    useState<string | null>(null)
  const [documentSettings, setDocumentSettings] =
    useState<DocumentExportSettings>(DEFAULT_DOCUMENT_EXPORT_SETTINGS)

  const handleModalTurnstileVerify = useCallback((token: string) => {
    setTurnstileToken(token)
    setPendingAutoDownload(true)
    setIsVerificationModalOpen(false)
  }, [])
  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken(null)
  }, [])

  useEffect(() => {
    const existingPreconnect = document.querySelector<HTMLLinkElement>(
      `link[rel="preconnect"][href="${TURNSTILE_ORIGIN}"]`,
    )
    if (existingPreconnect) return

    const link = document.createElement('link')
    link.rel = 'preconnect'
    link.href = TURNSTILE_ORIGIN
    link.crossOrigin = 'anonymous'
    document.head.appendChild(link)
  }, [])

  const {
    markdownContent,
    setMarkdownContent,
    fileName,
    isConverting,
    setIsConverting,
    isFullscreen,
    enterFullscreen,
    exitFullscreen,
    dropzone,
    uploadState,
    sourceScrollRef,
    previewScrollRef,
    handleSourceScroll,
  } = useMarkdownConverterCommon({
    useTransition: true,
    maxFileSizeBytes: PDF_MARKDOWN_MAX_SIZE_BYTES,
  })

  const downloadBlob = (blob: Blob, suggestedName?: string | null) => {
    const outputFileName = (suggestedName || 'converted-document')
      .replace(/\.(md|markdown|txt)$/i, '')
      .concat('.pdf')
    const blobUrl = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = blobUrl
    link.download = outputFileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(blobUrl)
  }

  const convertToPdf = useCallback(
    async (markdownValue = markdownContent) => {
      if (!markdownValue.trim()) {
        alert(t('errors.noContent'))
        return
      }
      if (isPdfMarkdownTooLarge(markdownValue)) {
        alert(t('errors.contentTooLarge'))
        return
      }

      trackGtagEvent('download_click', {
        event_category: 'download',
        event_label: 'md_pdf_button',
      })
      trackPlausibleEvent('download_click', {
        event_category: 'download',
        event_label: 'md_pdf_button',
      })

      setIsConverting(true)
      let requestId: string | null = null

      try {
        const response = await fetch(PDF_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            markdown: markdownValue,
            fileName,
            turnstileToken,
            documentSettings,
          }),
        })

        requestId = response.headers.get('X-PDF-Request-Id')
        if (!response.ok) {
          const message =
            (await response.text()) || t('errors.conversionFailed')
          const rateLimitReason = response.headers.get(
            'X-PDF-Rate-Limit-Reason',
          )
          const verificationRequired = response.headers.get(
            'X-PDF-Verification-Required',
          )
          if (
            response.status === 429 &&
            verificationRequired === 'turnstile' &&
            !turnstileToken
          ) {
            setPendingVerificationMarkdown(markdownValue)
            setIsVerificationModalOpen(true)
            trackTurnstileEvent('turnstile_server_verify_required', {
              status: response.status,
            })
            return
          }
          if (message.toLowerCase().includes('turnstile')) {
            trackTurnstileEvent('turnstile_server_verify_failed', {
              status: response.status,
            })
          }
          if (rateLimitReason === 'pdf-ip-in-flight') {
            throw new Error(t('errors.alreadyProcessing'))
          }
          if (
            rateLimitReason === 'pdf-queue-full' ||
            rateLimitReason === 'pdf-queue-timeout'
          ) {
            throw new Error(t('errors.busy'))
          }
          if (rateLimitReason === 'pdf-rate-limited') {
            throw new Error(t('errors.rateLimited'))
          }
          if (response.status === 413)
            throw new Error(t('errors.contentTooLarge'))
          if (response.status === 504) throw new Error(t('errors.timedOut'))
          if (response.status >= 500) throw new Error(t('errors.serverFailed'))
          throw new Error(message)
        }

        const blob = await response.blob()
        downloadBlob(blob, fileName || 'converted-document')
        const failedCount = Number(response.headers.get('X-PDF-Images-Failed'))
        if (Number.isFinite(failedCount) && failedCount > 0) {
          let failures: Record<string, number> = {}
          try {
            failures =
              JSON.parse(
                response.headers.get('X-PDF-Image-Failures') || '{}',
              ) ?? {}
          } catch {}
          const reasons = [
            'relative',
            'timeout',
            'blocked',
            'notFound',
            'tooLarge',
            'unsupported',
            'upstream',
            'network',
            'decode',
          ]
          const details = reasons
            .filter((reason) => Number(failures[reason]) > 0)
            .map((reason) =>
              t(`imageWarnings.${reason}`, { count: failures[reason] }),
            )
          alert(
            [
              t('imageWarnings.partial', { count: failedCount }),
              ...details,
            ].join('\n'),
          )
        }
      } catch (error) {
        console.error('Failed to convert markdown to PDF', error)
        const message =
          error instanceof TypeError
            ? t('errors.networkFailed')
            : error instanceof Error && error.message
              ? error.message
              : t('errors.conversionFailed')
        alert(
          requestId
            ? `${message}\n${t('errors.requestId', { id: requestId })}`
            : message,
        )
      } finally {
        setIsConverting(false)
        setTurnstileToken(null)
        setTurnstileRefreshKey((value) => value + 1)
      }
    },
    [
      documentSettings,
      fileName,
      markdownContent,
      t,
      setIsConverting,
      turnstileToken,
    ],
  )

  const handleConvertToPdf = useCallback(
    (markdownValue?: string) => {
      const runConvert = () => convertToPdf(markdownValue)
      if (!guardDownload(runConvert)) return
      runConvert()
    },
    [guardDownload, convertToPdf],
  )

  const handleConvertToPdfClick = useCallback(() => {
    handleConvertToPdf()
  }, [handleConvertToPdf])

  const handlePdfToMarkdownCtaClick = useCallback(() => {
    trackGtagEvent('pdf_to_markdown_cta_click', {
      event_category: 'cross_link',
      event_label: 'markdown_to_pdf_converter',
    })
    trackPlausibleEvent('pdf_to_markdown_cta_click', {
      event_category: 'cross_link',
      event_label: 'markdown_to_pdf_converter',
    })
  }, [])

  useEffect(() => {
    if (pendingAutoDownload && turnstileToken) {
      convertToPdf(pendingVerificationMarkdown || undefined)
      setPendingAutoDownload(false)
      setPendingVerificationMarkdown(null)
    }
  }, [
    pendingAutoDownload,
    pendingVerificationMarkdown,
    turnstileToken,
    convertToPdf,
  ])

  const handleVerificationModalClose = useCallback(() => {
    setIsVerificationModalOpen(false)
    setPendingAutoDownload(false)
    setPendingVerificationMarkdown(null)
  }, [])

  return (
    <>
      <MarkdownConverterShell
        markdownContent={markdownContent}
        exampleMarkdown={exampleMarkdown}
        onMarkdownChange={setMarkdownContent}
        onLoadExample={() => setMarkdownContent(exampleMarkdown)}
        dropzone={dropzone}
        uploadState={uploadState}
        uploadAriaLabel={t('upload.title')}
        uploadCopy={{
          dragActive: t('upload.dragActive'),
          dragInactive: t('upload.dragInactive'),
          fileConstraints: t('upload.fileConstraints'),
        }}
        headings={{
          upload: {
            label: t('upload.title'),
            icon: <Upload className="mr-2 h-5 w-5" />,
            className: 'flex items-center gap-2 text-xl font-semibold',
          },
          editor: {
            label: t('editor.title'),
            icon: <FileText className="h-5 w-5" />,
            className: 'flex items-center gap-2 text-xl font-semibold',
          },
          preview: {
            label: t('preview.title'),
            icon: <Eye className="h-5 w-5" />,
            className: 'flex items-center gap-2 text-xl font-semibold',
          },
        }}
        loadExampleLabel={t('editor.loadExample')}
        lineCountLabel={t('editor.lines')}
        uploadDropIcon={
          <FileText className="mx-auto mb-4 text-muted-foreground" size={40} />
        }
        previewPlaceholder={t('preview.placeholder')}
        previewExportSettings={documentSettings}
        previewToolbarActions={
          <DocumentExportSettingsButton
            settings={documentSettings}
            onChange={setDocumentSettings}
          />
        }
        onEnterFullscreen={enterFullscreen}
        onConvert={handleConvertToPdfClick}
        isConverting={isConverting}
        disableConvert={false}
        convertLabels={{
          idle: t('conversion.downloadPdf'),
          busy: t('conversion.converting'),
        }}
        actionHelperText={
          <p className="text-center text-sm text-muted-foreground">
            {t.rich('pdfToMarkdownCta.text', {
              link: (chunks) => (
                <a
                  href={PDF_TO_MARKDOWN_URL}
                  target="_blank"
                  rel="noopener"
                  onClick={handlePdfToMarkdownCtaClick}
                  aria-label={t('pdfToMarkdownCta.ariaLabel')}
                  className="inline-flex items-center font-medium text-primary underline-offset-4 hover:underline"
                >
                  {chunks}
                  <ExternalLink className="ml-1 h-3.5 w-3.5" aria-hidden />
                </a>
              ),
            })}
          </p>
        }
        fullscreenViewer={
          <MarkdownFullscreenViewer
            open={isFullscreen}
            onClose={exitFullscreen}
            markdown={markdownContent}
            fileName={fileName || 'markdown-to-pdf'}
            onSave={(newMarkdown) => setMarkdownContent(newMarkdown)}
            onDownload={handleConvertToPdf}
            allowEdit
            downloadDisabled={isConverting}
            previewExportSettings={documentSettings}
            labels={{
              title: 'Markdown To PDF - Fullscreen',
              source: tc('actions.source') || 'Source',
              preview: tc('actions.preview') || 'Preview',
              mobileToggleToPreview: tc('actions.preview') || 'Preview',
              mobileToggleToSource: tc('actions.source') || 'Source',
              copied: tc('actions.copied') || 'Copied!',
              copy: tc('actions.copy') || 'Copy',
              download: tc('actions.download') || 'Download',
              editing: tc('actions.editing') || 'Editing',
              live: tc('actions.live') || 'Live',
              linesSuffix: tc('actions.lines') || 'lines',
              exit: tc('fullscreen.exitFullscreen') || 'Exit',
            }}
          />
        }
        sourceScrollRef={sourceScrollRef}
        previewScrollRef={previewScrollRef}
        onSourceScroll={handleSourceScroll}
      />
      <VerificationModal
        isOpen={isVerificationModalOpen}
        onClose={handleVerificationModalClose}
        onVerify={handleModalTurnstileVerify}
        onExpire={handleTurnstileExpire}
        refreshKey={turnstileRefreshKey}
      />
      <InAppDownloadModal
        open={isInAppModalOpen}
        onClose={closeInAppModal}
        onContinue={continueWithDownload}
        appName={inAppInfo?.appName}
      />
    </>
  )
}
